import { useEffect, useMemo, useRef, useState } from 'react';
import './quests-landing.styles.scss';
import { changeDocumentTitle } from '@/utils/useDocumentTitle';
import { useSelector } from 'react-redux';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faHand } from '@fortawesome/free-regular-svg-icons';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { getCollectionFromFirestore, getQueryFromFirestore } from '@/utils/getDataFromDbSimplified';
import PlayerQuestItem from './player-quest-item.component';
import { properCase } from '@/utils/properCase';
import PlayerQuestPage from './player-quest-page.component';
import { checkElementAccess } from '@/utils/checkElementAccess';
import { completeGameElementData } from '@/components/PREVIEW/previewGameElements';

const PlayerQuestsLanding = ({
  gameData,
  playerData,
  gameElements,
  gameStats,
  teamMembers,
}) => {

  const screenSize = useSelector(state => state.contentData.screenSize);
  const [typeFilter, setTypeFilter] = useState('available');
  const [quests, setQuests] = useState({});
  const [playerProgress, setPlayerProgress] = useState({});
  const questsListenerRef = useRef(null);
  const progressListenerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const gameId = gameData?.gameId;
  const playerId = playerData?.playerId;
  const isPreview = location.pathname.includes('/preview/manager/');

  useEffect(() => {
    if (!gameData?.gameName) return;
    const pageTitle = document.getElementById('page-title');
    if (pageTitle) {
      pageTitle.innerText = 'Quests';
    }
    changeDocumentTitle(`Quests | ${gameData?.gameName}`);
  }, [gameData?.gameName]);

  // Subscriptions only depend on which game/player is being viewed, so changes
  // to gameStats, playerData, etc. don't tear down and re-read the listeners.
  useEffect(() => {
    if (!gameId || !playerId) return;
    setQuests({});
    setPlayerProgress({});
    getQuestsFromDb();
    getPlayerProgressFromDb();

    function getQuestsFromDb() {
      if (isPreview) {
        if (!completeGameElementData) return;
        const questObj = {};
        Object.values(completeGameElementData)
          .filter(e => e.type === 'quest')
          .forEach(e => {
            questObj[e.id] = e;
          });
        setQuests(questObj);
        return;
      }
      getQueryFromFirestore({
        'path': `games/${gameId}/gameElements`,
        'queryArray': [
          {
            'field': 'type',
            'operator': '==',
            'value': 'quest'
          },
        ],
        'callback': callback,
        'handleListener': handleListener,
      });
      function callback(data) {
        // keep inactive quests too so a quest that gets deactivated replaces
        // its stale copy; they are filtered out when the list is built
        setQuests(prev => ({ ...prev, [data.id]: data }));
      }
      function handleListener(unsub) {
        questsListenerRef.current = unsub;
      }
    }

    function getPlayerProgressFromDb() {
      if (isPreview) {
        /**
         * GET PLAYER QUEST DATA FROM PREVIEW PLAYER DATA
         */
        return;
      }
      getCollectionFromFirestore({
        'path': `games/${gameId}/players/${playerId}/quests`,
        'callback': callback,
        'handleListener': handleListener,
      });
      function callback(data) {
        setPlayerProgress(prev => ({ ...prev, [data.questId]: data }));
      }
      function handleListener(unsub) {
        progressListenerRef.current = unsub;
      }
    }

    return () => {
      if (questsListenerRef.current) {
        questsListenerRef.current();
        questsListenerRef.current = null;
      }
      if (progressListenerRef.current) {
        progressListenerRef.current();
        progressListenerRef.current = null;
      }
    }
  }, [gameId, playerId, isPreview]);

  const questList = useMemo(() => {
    if (!gameElements || !playerData || !gameStats || !teamMembers) return [];
    return Object.values(quests)
      // hidden quests are not shown at all, not even as locked
      .filter(quest => quest.status === 'active' && quest.opt_visible)
      .map(quest => {
        const access = checkElementAccess({
          playerData: playerData,
          gameElements: { ...gameElements, [quest.id]: quest },
          elementId: quest.id,
          gameStats: gameStats,
          teamMembers: teamMembers,
          type: 'quest'
        });
        if (!access?.access) {
          return { ...quest, questStatus: 'locked', missingReqs: access?.arr || [] };
        }
        const progressStatus = playerProgress[quest.id]?.status;
        const status = (progressStatus === 'complete' || progressStatus === 'inProgress')
          ? progressStatus
          : 'available';
        return { ...quest, questStatus: status };
      });
  }, [quests, playerProgress, gameElements, playerData, gameStats, teamMembers]);

  const questsToDisplay = useMemo(() => {
    let filtered;
    if (typeFilter === 'available') {
      filtered = questList.filter(q => q.questStatus === 'available' || q.questStatus === 'inProgress');
    } else if (typeFilter === 'closed') {
      filtered = questList.filter(q => q.questStatus === 'locked');
    } else {
      filtered = questList.filter(q => q.questStatus === typeFilter);
    }
    return filtered.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1);
  }, [questList, typeFilter]);


  return (
    <div className={`player-quests ${screenSize}`}>
      <div className='player-quests-content'>
        <Routes>
          <Route path='' element={
            <div className='g-card'>
              <div className='player-title'>
                <ImageContainer
                  src='/images/icons/quests.png'
                  alt='quests icon'
                  className='player-title-icon'
                />
                Go On a Quest
              </div>
              <hr />
              <div className={`quests-grid ${screenSize}`}>
                <div className='left-col'>
                  <div className='quests-left-menu'>
                    <button
                      className={`g-button med-btn ${typeFilter === 'available' ? 'primary' : ''}`}
                      onClick={() => setTypeFilter('available')}
                    >
                      <FontAwesomeIcon icon={faHand} />
                      {screenSize !== 'mobile' ? 'Available' : ''}
                    </button>
                    <button
                      className={`g-button med-btn ${typeFilter === 'closed' ? 'primary' : ''}`}
                      onClick={() => setTypeFilter('closed')}
                    >
                      <FontAwesomeIcon icon={faLock} />
                      {screenSize !== 'mobile' ? 'Locked' : ''}
                    </button>
                    <button
                      className={`g-button med-btn ${typeFilter === 'complete' ? 'primary' : ''}`}
                      onClick={() => setTypeFilter('complete')}
                    >
                      <FontAwesomeIcon icon={faCheckCircle} />
                      {screenSize !== 'mobile' ? 'Complete' : ''}
                    </button>
                  </div>
                </div>
                <div className='right-col'>
                  {
                    (screenSize === 'mobile' || screenSize === 'tablet') &&
                    <div className='small-screen-header'>
                      <h2>{properCase(typeFilter)}</h2>
                      <hr />
                    </div>
                  }
                  <div className='player-quest-list-container'>
                    {
                      questsToDisplay.length > 0
                        ?
                        <div className={`player-quest-list ${screenSize}`}>
                          {
                            questsToDisplay
                              .map(quest => (
                                <div key={quest.id}>
                                  <PlayerQuestItem
                                    quest={quest}
                                    onClick={() => navigate(quest.path)}
                                  />
                                </div>
                              ))
                          }
                        </div>
                        :
                        <div>
                          <h4>No {typeFilter} quests...</h4>
                        </div>
                    }
                  </div>
                </div>
              </div>
            </div>
          } />
          <Route path=':questPath/*' element={
            <PlayerQuestPage
              gameData={gameData}
              gameElements={gameElements}
              gameStats={gameStats}
              teamMembers={teamMembers}
              playerData={playerData}
              playerProgress={playerProgress}
            />
          } />
        </Routes>
        <div className="g-space-2"></div>
      </div>
    </div>
  )
}

export default PlayerQuestsLanding
